import { useId, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  errorMessage,
  useConsoleConfig,
  useCreateAccount,
  useCreateDataset,
  useAccounts,
} from "../api/queries";
import { useApi } from "../api/ApiProvider";
import type {
  Account,
  ConsoleConfig,
  Dataset,
  EvidenceKind,
  FileFormat,
  Purpose,
  TaskType,
} from "../api/types";
import { PURPOSES } from "../api/types";
import { isStorageProblem, StorageProblem } from "../components/StorageProblem";
import { EvidenceFields } from "../components/dataset/EvidenceFields";
import { PageHeader } from "../components/layout/PageHeader";
import { Field } from "../components/ui/Field";
import { ErrorBlock, LoadingBlock } from "../components/ui/StateBlock";
import {
  formatFromFilename,
  nameFromFilename,
  readCsvColumns,
} from "../lib/csvHeader";
import {
  formatBytes,
  NOT_A_NUMBER,
  parseNumber,
  toSlug,
} from "../lib/format";
import {
  metricsFor,
  PURPOSE_SENTENCES,
  TASK_TYPE_HINTS,
  TASK_TYPE_LABELS,
} from "../lib/metrics";
import { useUploads } from "../upload/UploadProvider";

/** The four questions, in the order this product asks them. */
const STEPS = [
  "Whose data is this?",
  "The file",
  "What may we do with it?",
  "What are we trying to beat?",
] as const;

/** The answer every field of the last question accepts. */
const NOT_YET = "Not yet — the client has not told me";

interface StepperProps {
  step: number;
}

/** Where you are in the four questions. */
function Stepper({ step }: StepperProps): JSX.Element {
  return (
    <ol className="stepper">
      {STEPS.map((label, index) => (
        <li
          className="stepper__item"
          data-state={
            index === step ? "current" : index < step ? "done" : "todo"
          }
          key={label}
        >
          <span className="stepper__number mono">{index + 1}</span>
          <span className="stepper__label">{label}</span>
        </li>
      ))}
    </ol>
  );
}

interface AccountStepProps {
  accounts: Account[];
  selected: string;
  onSelect: (id: string) => void;
  onNext: () => void;
}

/** Question 1: whose data is this. Pick a client, or add one. */
function AccountStep({
  accounts,
  selected,
  onSelect,
  onNext,
}: AccountStepProps): JSX.Element {
  const nameId = useId();
  const slugId = useId();
  const [adding, setAdding] = useState(accounts.length === 0);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const create = useCreateAccount();

  return (
    <section className="wizard__step">
      <h2 className="wizard__title">Whose data is this?</h2>
      <p className="wizard__lead">
        The client this file belongs to. Everything else on the record hangs off
        it: what they allowed, what we have to beat, and who we owe a receipt to
        if they ask us to erase it.
      </p>

      {accounts.length > 0 ? (
        <div className="choice-row">
          {accounts.map((account) => (
            <label
              className="choice"
              key={account.id}
              data-selected={selected === account.id}
            >
              <input
                className="sr-only"
                type="radio"
                name="account"
                value={account.id}
                checked={selected === account.id}
                onChange={() => onSelect(account.id)}
              />
              <span className="choice__name">{account.name}</span>
              <span className="choice__meta mono">{account.slug}</span>
            </label>
          ))}
        </div>
      ) : null}

      {adding ? (
        <form
          className="form"
          onSubmit={(event) => {
            event.preventDefault();
            create.mutate(
              { name: name.trim(), slug: slug.trim() },
              {
                onSuccess: (account) => {
                  onSelect(account.id);
                  setAdding(false);
                  setName("");
                  setSlug("");
                },
              },
            );
          }}
        >
          <Field label="The client's name" htmlFor={nameId}>
            <input
              id={nameId}
              className="input"
              value={name}
              onChange={(event) => {
                setName(event.target.value);
                setSlug(toSlug(event.target.value));
              }}
              placeholder="Acme SA"
            />
          </Field>
          <Field
            label="A short name for it"
            htmlFor={slugId}
            hint="Lower-case, no spaces. It is frozen from the moment you add them: every file they ever give us is filed under it, and a receipt we owe them names it."
            error={create.isError ? errorMessage(create.error) : undefined}
          >
            <input
              id={slugId}
              className="input input--mono"
              value={slug}
              onChange={(event) => setSlug(toSlug(event.target.value))}
              placeholder="acme"
            />
          </Field>
          <div className="gate__actions">
            <button
              type="submit"
              className="btn btn--primary"
              disabled={
                create.isPending || name.trim() === "" || slug.trim() === ""
              }
            >
              {create.isPending ? "Adding…" : "Add this client"}
            </button>
            {accounts.length > 0 ? (
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => setAdding(false)}
              >
                Cancel
              </button>
            ) : null}
          </div>
        </form>
      ) : (
        <button
          type="button"
          className="btn btn--sm"
          onClick={() => setAdding(true)}
        >
          This client is not here yet
        </button>
      )}

      <div className="wizard__actions">
        <button
          type="button"
          className="btn btn--primary"
          disabled={selected === ""}
          onClick={onNext}
        >
          Next: the file
        </button>
      </div>
    </section>
  );
}

interface FileStepProps {
  config: ConsoleConfig | undefined;
  file: File | null;
  columns: string[];
  datasetName: string;
  format: FileFormat | null;
  onFile: (file: File, columns: string[], format: FileFormat | null) => void;
  onName: (name: string) => void;
  onBack: () => void;
  onNext: () => void;
}

/** Question 2: the file. Read here, in the browser, and nowhere else yet. */
function FileStep({
  config,
  file,
  columns,
  datasetName,
  format,
  onFile,
  onName,
  onBack,
  onNext,
}: FileStepProps): JSX.Element {
  const fileId = useId();
  const nameId = useId();
  const [reading, setReading] = useState(false);

  const take = async (chosen: File): Promise<void> => {
    setReading(true);
    const declaredFormat = formatFromFilename(chosen.name);
    const names =
      declaredFormat === "csv" ? await readCsvColumns(chosen) : [];
    onFile(chosen, names, declaredFormat);
    onName(nameFromFilename(chosen.name));
    setReading(false);
  };

  return (
    <section className="wizard__step">
      <h2 className="wizard__title">The file</h2>
      <p className="wizard__lead">
        One file, and it never changes afterwards: when a client resends a fixed
        one, that is the next version and this one still answers what a score
        came from.
      </p>

      <div
        className="dropzone"
        onDragOver={(event) => event.preventDefault()}
        onDrop={(event) => {
          event.preventDefault();
          const dropped = event.dataTransfer.files[0];
          if (dropped) {
            void take(dropped);
          }
        }}
      >
        <label className="dropzone__label" htmlFor={fileId}>
          Drop the file here, or choose one
        </label>
        <input
          id={fileId}
          className="dropzone__input"
          type="file"
          onChange={(event) => {
            const chosen = event.target.files?.[0];
            if (chosen) {
              void take(chosen);
            }
          }}
        />
        {config ? (
          <p className="form__note">
            CSV or Parquet, up to {config.max_upload_gb} GB. That is a bound on
            one file, not on you — there is no allowance of any kind in this
            product.
          </p>
        ) : null}
      </div>

      {reading ? <LoadingBlock label="Reading the first lines here…" /> : null}

      {file ? (
        <>
          <p className="wizard__file">
            <strong>{file.name}</strong> — {formatBytes(file.size)}
            {format ? (
              <> · you are telling the console this is a {format} file</>
            ) : (
              <>
                {" "}
                · the console takes CSV and Parquet, and cannot tell which this
                is from its name. It will read the file itself and say so if it
                disagrees.
              </>
            )}
          </p>
          <p className="form__note">
            {columns.length > 0
              ? `Your browser read the first lines of this file and found ${columns.length} columns. Those bytes have not left this machine — they are here so the last question can offer you the names instead of asking you to type one.`
              : "Nothing has been sent yet. The file starts going up when you have said what we may do with it and what kind of problem it is."}
          </p>

          <Field
            label="What should we call this dataset?"
            htmlFor={nameId}
            hint="A name a person recognises. Its short name is derived from this and frozen."
          >
            <input
              id={nameId}
              className="input"
              value={datasetName}
              onChange={(event) => onName(event.target.value)}
            />
          </Field>
        </>
      ) : null}

      <div className="wizard__actions">
        <button type="button" className="btn btn--ghost" onClick={onBack}>
          Back
        </button>
        <button
          type="button"
          className="btn btn--primary"
          disabled={!file || datasetName.trim() === ""}
          onClick={onNext}
        >
          Next: what may we do with it
        </button>
      </div>
    </section>
  );
}

interface ConsentStepProps {
  config: ConsoleConfig | undefined;
  accountName: string;
  permissions: Purpose[];
  evidence: string;
  evidenceKind: EvidenceKind | "";
  evidenceRef: string;
  onPermissions: (permissions: Purpose[]) => void;
  onEvidence: (evidence: string) => void;
  onEvidenceKind: (kind: EvidenceKind) => void;
  onEvidenceRef: (reference: string) => void;
  onBack: () => void;
  onNext: () => void;
}

/**
 * Question 3: what the client agreed to, where they said it, and what that was.
 *
 * The kind is asked here rather than inferred later: a source that does not say
 * what kind of thing it is makes a DPA clause and a corridor conversation read
 * identically on the day somebody relies on one of them (ADR-0013).
 */
function ConsentStep({
  config,
  accountName,
  permissions,
  evidence,
  evidenceKind,
  evidenceRef,
  onPermissions,
  onEvidence,
  onEvidenceKind,
  onEvidenceRef,
  onBack,
  onNext,
}: ConsentStepProps): JSX.Element {
  const evidenceId = useId();

  return (
    <section className="wizard__step">
      <h2 className="wizard__title">What may we do with it?</h2>
      <p className="wizard__lead">
        These are not our settings — they are what {accountName} agreed to.
        Nothing is ticked for you: a checkbox somebody did not clear is not a
        consent.
      </p>

      <fieldset className="form__block">
        <legend className="sr-only">What the client allows</legend>
        {PURPOSES.map((purpose) => (
          <label className="check check--wide" key={purpose}>
            <input
              type="checkbox"
              checked={permissions.includes(purpose)}
              onChange={(event) =>
                onPermissions(
                  event.target.checked
                    ? [...permissions, purpose].sort(
                        (left, right) =>
                          PURPOSES.indexOf(left) - PURPOSES.indexOf(right),
                      )
                    : permissions.filter((item) => item !== purpose),
                )
              }
            />
            <span>{PURPOSE_SENTENCES[purpose]}</span>
          </label>
        ))}
      </fieldset>

      <Field
        label="Where does this permission come from?"
        htmlFor={evidenceId}
        hint="A contract clause, an e-mail and its date, or a DPA or NDA reference. A permission with no attributable source is a permission somebody invented."
      >
        <input
          id={evidenceId}
          className="input"
          value={evidence}
          onChange={(event) => onEvidence(event.target.value)}
          placeholder="DPA amendment signed 3 July 2026, C. Meyer"
        />
      </Field>

      <EvidenceFields
        config={config}
        kind={evidenceKind}
        reference={evidenceRef}
        onKind={onEvidenceKind}
        onReference={onEvidenceRef}
      />

      <div className="wizard__actions">
        <button type="button" className="btn btn--ghost" onClick={onBack}>
          Back
        </button>
        <button
          type="button"
          className="btn btn--primary"
          disabled={
            permissions.length === 0 ||
            evidence.trim() === "" ||
            evidenceKind === ""
          }
          onClick={onNext}
        >
          Next: what we have to beat
        </button>
      </div>
    </section>
  );
}

/**
 * Add a dataset: four questions, and the file goes up behind the last one.
 *
 * The order is the product's own. The one thing this screen cannot do is start
 * the transfer at question 2: a file belongs to a version, a version belongs to
 * a dataset, and a dataset cannot be recorded without the client, the consent
 * **and** the kind of problem it is (`docs/API.md`, `POST /datasets`). So the
 * console records everything it has as soon as it legally can — at the top of
 * question 4 — and the file starts moving there, behind the bar, the baseline
 * and the evidence. Every screen after that carries the transfer with it.
 *
 * Returns:
 *     The wizard.
 */
export function NewDatasetPage(): JSX.Element {
  const api = useApi();
  const navigate = useNavigate();
  const uploads = useUploads();
  const accounts = useAccounts();
  const config = useConsoleConfig();
  const createDataset = useCreateDataset();

  const targetColumnId = useId();
  const typedColumnId = useId();
  const metricId = useId();
  const thresholdId = useId();
  const baselineId = useId();
  const baselineReasonId = useId();
  const barEvidenceId = useId();

  const [step, setStep] = useState(0);
  const [accountId, setAccountId] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [columns, setColumns] = useState<string[]>([]);
  const [format, setFormat] = useState<FileFormat | null>(null);
  const [datasetName, setDatasetName] = useState("");
  const [permissions, setPermissions] = useState<Purpose[]>([]);
  const [evidence, setEvidence] = useState("");
  const [evidenceKind, setEvidenceKind] = useState<EvidenceKind | "">("");
  const [evidenceRef, setEvidenceRef] = useState("");
  const [taskType, setTaskType] = useState<TaskType | "">("");
  const [targetColumn, setTargetColumn] = useState("");
  const [created, setCreated] = useState<Dataset | null>(null);
  const [startError, setStartError] = useState<unknown>(null);
  const [starting, setStarting] = useState(false);

  const [barWanted, setBarWanted] = useState(false);
  const [metric, setMetric] = useState("");
  const [threshold, setThreshold] = useState("");
  const [baselineKnown, setBaselineKnown] = useState(false);
  const [baseline, setBaseline] = useState("");
  const [baselineReason, setBaselineReason] = useState("");
  const [barEvidence, setBarEvidence] = useState("");
  const [barError, setBarError] = useState<unknown>(null);
  const [finishing, setFinishing] = useState(false);

  const header = (
    <PageHeader
      eyebrow="Add a dataset"
      title="Four questions"
      subtitle="Answer them once, and the file has an owner, a permission, a bar and a receipt."
      back={{ to: "/", label: "All clients" }}
    />
  );

  if (accounts.isPending) {
    return (
      <>
        {header}
        <LoadingBlock label="Reading the client list…" />
      </>
    );
  }

  if (isStorageProblem(accounts.error)) {
    return (
      <>
        {header}
        <StorageProblem
          error={accounts.error}
          onRetry={() => void accounts.refetch()}
        />
      </>
    );
  }

  if (accounts.isError) {
    return (
      <>
        {header}
        <ErrorBlock
          error={accounts.error}
          onRetry={() => void accounts.refetch()}
        />
      </>
    );
  }

  const account = accounts.data.items.find((item) => item.id === accountId);
  const metricOptions =
    taskType === "" ? [] : metricsFor(config.data, taskType);

  /** Record everything answered so far, open the version, and start sending. */
  const recordAndSend = async (): Promise<void> => {
    if (!file || taskType === "" || evidenceKind === "" || !account) {
      return;
    }
    setStarting(true);
    setStartError(null);
    try {
      const dataset =
        created ??
        (await createDataset.mutateAsync({
          account_id: account.id,
          name: datasetName.trim(),
          task_type: taskType,
          target_column: targetColumn.trim() ? targetColumn.trim() : null,
          consent: {
            permissions,
            evidence: evidence.trim(),
            evidence_kind: evidenceKind,
            // Nothing to point at is `null`, never "n/a": null is an honest
            // absence and "n/a" is text somebody has to interpret later.
            evidence_ref: evidenceRef.trim() ? evidenceRef.trim() : null,
          },
        }));
      setCreated(dataset);
      const version = await api.createVersion(dataset.id, {
        filename: file.name,
        // The console reads the file itself and says so if it disagrees; a
        // name is a declaration, so an unrecognised extension is declared as
        // the one this console takes and settled by the bytes.
        format: format ?? "csv",
        size_bytes: file.size,
      });
      if (version.upload) {
        uploads.start({
          datasetId: dataset.id,
          datasetLabel: `${dataset.account.name} · ${dataset.name}`,
          versionNumber: version.number,
          file,
          format: format ?? "csv",
          uploadId: version.upload.upload_id,
          parts: version.upload.parts,
        });
      }
    } catch (error) {
      setStartError(error);
    } finally {
      setStarting(false);
    }
  };

  // Parsed once, here, so the button and the request agree about what is in
  // the boxes: a comma decimal is a number, and an empty box is not a zero.
  const wantedValue = parseNumber(threshold);
  const baselineValue = parseNumber(baseline);

  /** Record the bar if there is one, then go and look at the dataset. */
  const finish = async (): Promise<void> => {
    if (!created) {
      return;
    }
    setFinishing(true);
    setBarError(null);
    try {
      if (barWanted) {
        if (wantedValue === null || (baselineKnown && baselineValue === null)) {
          return;
        }
        await api.recordTarget(created.id, {
          metric,
          threshold_value: wantedValue,
          // An empty box is never a zero. `Number("")` is one, which is how a
          // mis-click became a permanent "their model scores 0".
          baseline_value: baselineKnown ? baselineValue : null,
          baseline_absent_reason: baselineKnown
            ? null
            : baselineReason.trim() || NOT_YET,
          evidence: barEvidence.trim(),
        });
      }
      navigate(`/datasets/${created.id}`);
    } catch (error) {
      setBarError(error);
    } finally {
      setFinishing(false);
    }
  };

  return (
    <>
      {header}
      <Stepper step={step} />

      {step === 0 ? (
        <AccountStep
          accounts={accounts.data.items}
          selected={accountId}
          onSelect={setAccountId}
          onNext={() => setStep(1)}
        />
      ) : null}

      {step === 1 ? (
        <FileStep
          config={config.data}
          file={file}
          columns={columns}
          datasetName={datasetName}
          format={format}
          onFile={(chosen, names, declared) => {
            setFile(chosen);
            setColumns(names);
            setFormat(declared);
          }}
          onName={setDatasetName}
          onBack={() => setStep(0)}
          onNext={() => setStep(2)}
        />
      ) : null}

      {step === 2 ? (
        <ConsentStep
          config={config.data}
          accountName={account?.name ?? "the client"}
          permissions={permissions}
          evidence={evidence}
          evidenceKind={evidenceKind}
          evidenceRef={evidenceRef}
          onPermissions={setPermissions}
          onEvidence={setEvidence}
          onEvidenceKind={setEvidenceKind}
          onEvidenceRef={setEvidenceRef}
          onBack={() => setStep(1)}
          onNext={() => setStep(3)}
        />
      ) : null}

      {step === 3 ? (
        <section className="wizard__step">
          <h2 className="wizard__title">What are we trying to beat?</h2>

          {/*
            Outside both halves on purpose. A console that could not take the
            file still recorded the dataset, and hiding the refusal behind the
            step it happened in would leave somebody wondering where their file
            went.
          */}
          {startError ? (
            <p className="form__error" role="alert">
              {errorMessage(startError)}
            </p>
          ) : null}

          {created ? null : (
            <>
              <p className="wizard__lead">
                Two answers first, because the console cannot open a record
                without them — and the file starts going up the moment it has
                them.
              </p>

              <fieldset className="form__block">
                <legend className="form__legend">
                  What kind of problem is it?
                </legend>
                <div className="choice-row">
                  {(config.data?.task_types ?? []).map((type) => (
                    <label
                      className="choice"
                      key={type}
                      data-selected={taskType === type}
                    >
                      <input
                        className="sr-only"
                        type="radio"
                        name="task-type"
                        value={type}
                        checked={taskType === type}
                        onChange={() => setTaskType(type)}
                      />
                      <span className="choice__name">
                        {TASK_TYPE_LABELS[type]}
                      </span>
                      <span className="choice__meta">
                        {TASK_TYPE_HINTS[type]}
                      </span>
                    </label>
                  ))}
                </div>
                {config.data ? null : (
                  <p className="form__note">
                    The console has not published which kinds of problem it
                    knows about, so there is nothing to pick from yet.
                  </p>
                )}
              </fieldset>

              {columns.length > 0 ? (
                <Field
                  label="Which column holds the answer?"
                  htmlFor={targetColumnId}
                  hint="Picked from the names your browser read out of the file. Getting it wrong never rejects anything — the console says so and you correct it."
                >
                  <select
                    id={targetColumnId}
                    className="select"
                    value={targetColumn}
                    onChange={(event) => setTargetColumn(event.target.value)}
                  >
                    <option value="">{NOT_YET}</option>
                    {columns.map((column) => (
                      <option key={column} value={column}>
                        {column}
                      </option>
                    ))}
                  </select>
                </Field>
              ) : (
                <Field
                  label="Which column holds the answer?"
                  htmlFor={typedColumnId}
                  hint="Your browser could not read the names out of this file, so type it — or leave it, which is a real answer. The console checks it against the file and never rejects one for it."
                >
                  <input
                    id={typedColumnId}
                    className="input input--mono"
                    value={targetColumn}
                    onChange={(event) => setTargetColumn(event.target.value)}
                    placeholder={NOT_YET}
                  />
                </Field>
              )}

              <div className="wizard__actions">
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={() => setStep(2)}
                >
                  Back
                </button>
                <button
                  type="button"
                  className="btn btn--primary"
                  disabled={starting || taskType === ""}
                  onClick={() => void recordAndSend()}
                >
                  {starting
                    ? "Recording…"
                    : "Record this and start sending the file"}
                </button>
              </div>
            </>
          )}

          {created ? (
            <>
              <p className="wizard__lead">
                {startError
                  ? "The dataset is recorded, but the file did not start going up. You can try again here, or open the dataset and add the file from there — nothing you answered is lost."
                  : "Recorded, and your file is going up while you finish this. If the client has not given you a bar yet, that is a real answer — leave it and add one the day they do."}
              </p>

              {startError ? (
                <button
                  type="button"
                  className="btn btn--sm"
                  disabled={starting}
                  onClick={() => void recordAndSend()}
                >
                  {starting ? "Trying…" : "Try sending the file again"}
                </button>
              ) : null}

              <label className="check check--wide">
                <input
                  type="checkbox"
                  checked={barWanted}
                  onChange={(event) => {
                    setBarWanted(event.target.checked);
                    if (
                      event.target.checked &&
                      metric === "" &&
                      metricOptions[0]
                    ) {
                      setMetric(metricOptions[0].metric);
                    }
                  }}
                />
                <span>The client has told me what we have to beat</span>
              </label>

              {barWanted ? (
                <>
                  <Field
                    label="Which measure?"
                    htmlFor={metricId}
                    hint="Only the ones that belong to this kind of problem. The console refuses the others by name, and that is deliberate."
                  >
                    <select
                      id={metricId}
                      className="select"
                      value={metric}
                      onChange={(event) => setMetric(event.target.value)}
                    >
                      {metricOptions.map((option) => (
                        <option key={option.metric} value={option.metric}>
                          {option.label}
                          {option.direction === "higher_is_better"
                            ? " — higher is better"
                            : " — lower is better"}
                        </option>
                      ))}
                    </select>
                  </Field>

                  <Field
                    label="The bar"
                    htmlFor={thresholdId}
                    error={
                      threshold.trim() !== "" && wantedValue === null
                        ? NOT_A_NUMBER
                        : undefined
                    }
                  >
                    <input
                      id={thresholdId}
                      className="input input--narrow mono"
                      value={threshold}
                      inputMode="decimal"
                      onChange={(event) => setThreshold(event.target.value)}
                    />
                  </Field>

                  <label className="check">
                    <input
                      type="checkbox"
                      checked={baselineKnown}
                      onChange={(event) =>
                        setBaselineKnown(event.target.checked)
                      }
                    />
                    <span>We know what their own model scores</span>
                  </label>

                  {baselineKnown ? (
                    <Field
                      label="Their own model's score"
                      htmlFor={baselineId}
                      hint="A different number from the bar; clients routinely set the bar above their own."
                      error={
                        baseline.trim() !== "" && baselineValue === null
                          ? NOT_A_NUMBER
                          : undefined
                      }
                    >
                      <input
                        id={baselineId}
                        className="input input--narrow mono"
                        value={baseline}
                        inputMode="decimal"
                        onChange={(event) => setBaseline(event.target.value)}
                      />
                    </Field>
                  ) : (
                    <Field
                      label="Why there is no baseline"
                      htmlFor={baselineReasonId}
                      hint="An invented baseline is the worst number in the building, so the console keeps the reason instead."
                    >
                      <input
                        id={baselineReasonId}
                        className="input"
                        value={baselineReason}
                        onChange={(event) =>
                          setBaselineReason(event.target.value)
                        }
                        placeholder={NOT_YET}
                      />
                    </Field>
                  )}

                  <Field
                    label="Where did the bar come from?"
                    htmlFor={barEvidenceId}
                    hint="A call, a mail, a slide with a date on it."
                  >
                    <input
                      id={barEvidenceId}
                      className="input"
                      value={barEvidence}
                      onChange={(event) => setBarEvidence(event.target.value)}
                      placeholder="Kick-off call, 30 July 2026"
                    />
                  </Field>
                </>
              ) : (
                <p className="form__note">
                  No bar, then. The dataset says so honestly — it is a state,
                  never a zero — and the deal gate will read “no bar” until
                  somebody records one.
                </p>
              )}

              {barError ? (
                <p className="form__error" role="alert">
                  {errorMessage(barError)}
                </p>
              ) : null}

              <div className="wizard__actions">
                <button
                  type="button"
                  className="btn btn--primary"
                  disabled={
                    finishing ||
                    (barWanted &&
                      (metric === "" ||
                        wantedValue === null ||
                        barEvidence.trim() === "" ||
                        (baselineKnown && baselineValue === null)))
                  }
                  onClick={() => void finish()}
                >
                  {finishing ? "Finishing…" : "Done — open the dataset"}
                </button>
              </div>
            </>
          ) : null}
        </section>
      ) : null}
    </>
  );
}
